import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { api } from '../api/client';
import { useAnalyses } from '../analyses/AnalysesContext';
import { useAuth } from '../auth/AuthContext';


function formatPrice(usd, minutes) {
  if (usd === null || usd === undefined) return '—';
  if (usd < 0.01) return `<1 ¢/${minutes} min`;
  return `~$${usd.toFixed(2)}/${minutes} min`;
}


export default function Uploader() {
  const { upload } = useAnalyses();
  const { user } = useAuth();
  const canChooseModel = !!user?.can_choose_model;
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState(null);
  const [selected, setSelected] = useState(new Set());  // model-ids
  const [defaultModel, setDefaultModel] = useState(null);
  const [models, setModels] = useState([]);
  const [audioMinutes, setAudioMinutes] = useState(30);

  useEffect(() => {
    if (!canChooseModel) return;
    api.get('/models').then((r) => {
      setModels(r.data.models);
      setAudioMinutes(r.data.audio_minutes_base);
      const def = r.data.default_model;
      // Nur vorauswaehlen, wenn das Default in der Whitelist ist – sonst
      // greift der Fallback-Effect (erstes Modell der Liste).
      if (def && r.data.models.some((m) => m.id === def)) {
        setDefaultModel(def);
        setSelected((prev) => (prev.size === 0 ? new Set([def]) : prev));
      }
    }).catch(() => {});
  }, [canChooseModel]);

  // Fallback: wenn kein Default und Modelle geladen, nimm erstes
  useEffect(() => {
    if (selected.size === 0 && models.length > 0) {
      setSelected(new Set([models[0].id]));
    }
  }, [models, selected]);

  const toggleModel = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        if (next.size > 1) next.delete(id);  // mindestens 1 behalten
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const m of models) {
      g[m.category] = g[m.category] || [];
      g[m.category].push(m);
    }
    return g;
  }, [models]);

  const totalCost = useMemo(() => {
    const chosen = models.filter((m) => selected.has(m.id));
    const sum = chosen.reduce((s, m) => s + (m.estimated_cost_30min_usd || 0), 0);
    return sum;
  }, [models, selected]);

  const onDrop = useCallback(async (files) => {
    setError(null);
    setUploading(true);
    try {
      for (const f of files) {
        setCurrentFile(f.name);
        setProgress(0);
        await upload(f, (pct) => setProgress(pct), [...selected]);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      setProgress(0);
      setCurrentFile(null);
    }
  }, [upload, selected]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'audio/*': ['.mp3', '.m4a', '.wav', '.flac', '.ogg', '.webm', '.aac', '.mp4'],
    },
    maxSize: 100 * 1024 * 1024,
    disabled: uploading || (canChooseModel && selected.size === 0),
  });

  return (
    <div>
      {canChooseModel && (
        <div className="model-tags">
          <div className="tags-header">
            <span className="tags-title">Modelle</span>
            <span className="tags-summary">
              {selected.size} ausgewählt · ~${totalCost.toFixed(2)}/{audioMinutes} min
            </span>
          </div>
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat} className={`tags-row tags-${cat.toLowerCase()}`}>
              <span className="tags-group-label">{cat}</span>
              {list.map((m) => {
                const active = selected.has(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => toggleModel(m.id)}
                    disabled={uploading}
                    className={`tag${active ? ' tag-active' : ''}${m.id === defaultModel ? ' tag-default' : ''}`}
                    title={m.id === defaultModel ? 'Default-Modell' : m.label}
                  >
                    <span className="tag-label">{m.label}</span>
                    <span className="tag-price">{formatPrice(m.estimated_cost_30min_usd, audioMinutes)}</span>
                    {m.id === defaultModel && <span className="tag-star">★</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}

      <div {...getRootProps()} className={`dropzone${isDragActive ? ' active' : ''}${uploading ? ' busy' : ''}`}>
        <input {...getInputProps()} />
        {uploading ? (
          <div className="upload-state">
            <p>📤 Lade hoch: <strong>{currentFile}</strong> · {progress} %</p>
            <div className="upload-bar">
              <div className="upload-fill" style={{ width: `${progress}%` }} />
            </div>
            {progress >= 100 && <p className="meta">Datei übertragen – {selected.size} Analyse(n) starten gleich …</p>}
          </div>
        ) : (
          <p>⬆ Audiodatei hierher ziehen oder klicken (mp3, m4a, wav … bis 100 MB)</p>
        )}
        {error && <div className="error">{error}</div>}
      </div>
    </div>
  );
}
